import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, ReferenceLine, Tooltip } from 'recharts';

const API_BASE_URL = "https://weight-g2gtd9hhdhbudvf8.westus3-01.azurewebsites.net/api";

const formatWeight = (value) =>
  new Intl.NumberFormat(undefined, { maximumFractionDigits: 1, minimumFractionDigits: 0 }).format(value);

const formatDate = (date) =>
  date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

const parseTimestamp = (value) => {
  const timestamp = Date.parse(value);
  if (Number.isNaN(timestamp)) {
    return null;
  }
  return new Date(timestamp);
};

const linearRegression = (points) => {
  if (!points || points.length < 2) return null;
  const baseX = points[0].x;
  const MS_PER_DAY = 86400000;
  const normalized = points.map(p => ({ x: (p.x - baseX) / MS_PER_DAY, y: p.y }));
  const n = normalized.length;
  let sumX = 0, sumY = 0, sumXY = 0, sumXX = 0;
  for (const { x, y } of normalized) {
    sumX += x;
    sumY += y;
    sumXY += x * y;
    sumXX += x * x;
  }
  const denom = n * sumXX - sumX * sumX;
  if (denom === 0) return null;
  const slope = (n * sumXY - sumX * sumY) / denom;
  const intercept = (sumY - slope * sumX) / n;
  return { slope, intercept, baseX };
};

function App() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showExtrapolation, setShowExtrapolation] = useState(false);
  const [rangeFrom, setRangeFrom] = useState('');
  const [rangeTo, setRangeTo] = useState('');
  const [projectionDays, setProjectionDays] = useState(30);

  // Get username from URL param
  const username = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('user') || 'bwhybel';
  }, []);

  const normalizedEntries = useMemo(() => {
    if (!Array.isArray(entries)) {
      return [];
    }

    return entries
      .map((entry, index) => {
        const weight = Number(entry.weight);
        const parsedDate = parseTimestamp(entry.timestamp);

        if (!Number.isFinite(weight) || !parsedDate) {
          return null;
        }

        return {
          id: entry.id ?? `${parsedDate.getTime()}-${index}`,
          weight,
          timestampMs: parsedDate.getTime(),
          dateLabel: formatDate(parsedDate),
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.timestampMs - b.timestampMs);
  }, [entries]);

  const fetchWeightData = useCallback(async (isInitialLoad = false) => {
    if (isInitialLoad) {
      setLoading(true);
    }
    setError('');

    try {
      const response = await axios.get(`${API_BASE_URL}/weights/${encodeURIComponent(username)}`);
      const data = Array.isArray(response.data) ? response.data : [];
      setEntries(data);
    } catch (err) {
      // Only show error if no existing data
      if (isInitialLoad) {
        setError('Failed to load data');
        setEntries([]);
      }
    } finally {
      if (isInitialLoad) {
        setLoading(false);
      }
    }
  }, [username]);

  useEffect(() => {
    fetchWeightData(true);

    // Refresh every 30 seconds (silent background refresh)
    const interval = setInterval(() => fetchWeightData(false), 30 * 1000);
    return () => clearInterval(interval);
  }, [fetchWeightData]);

  const chartData = useMemo(() => {
    if (!showExtrapolation || normalizedEntries.length < 2) {
      return normalizedEntries;
    }

    const MS_PER_DAY = 86400000;
    let filtered = normalizedEntries;

    if (rangeFrom || rangeTo) {
      const fromMs = rangeFrom ? new Date(rangeFrom + 'T00:00:00').getTime() : -Infinity;
      const toMs = rangeTo ? new Date(rangeTo + 'T23:59:59').getTime() : Infinity;
      filtered = normalizedEntries.filter(e => e.timestampMs >= fromMs && e.timestampMs <= toMs);
    }

    const reg = linearRegression(filtered.map(e => ({ x: e.timestampMs, y: e.weight })));
    if (!reg) return normalizedEntries;

    const lastEntry = normalizedEntries[normalizedEntries.length - 1];
    const projectedPoints = [];
    for (let d = 1; d <= projectionDays; d++) {
      const ms = lastEntry.timestampMs + d * MS_PER_DAY;
      const daysFromBase = (ms - reg.baseX) / MS_PER_DAY;
      const projected = reg.intercept + reg.slope * daysFromBase;
      projectedPoints.push({
        timestampMs: ms,
        dateLabel: formatDate(new Date(ms)),
        projectedWeight: Math.round(projected * 10) / 10,
      });
    }

    // Add projectedWeight to last actual point so lines connect
    const mergedActual = normalizedEntries.map((e, i) => {
      if (i === normalizedEntries.length - 1) {
        return { ...e, projectedWeight: e.weight };
      }
      return e;
    });

    return [...mergedActual, ...projectedPoints];
  }, [normalizedEntries, showExtrapolation, rangeFrom, rangeTo, projectionDays]);

  const latestWeight = normalizedEntries.length > 0
    ? normalizedEntries[normalizedEntries.length - 1].weight
    : null;

  const allWeights = useMemo(() => {
    const weights = normalizedEntries.map(e => e.weight);
    if (showExtrapolation) {
      chartData.forEach(e => {
        if (e.projectedWeight != null) weights.push(e.projectedWeight);
      });
    }
    return weights;
  }, [normalizedEntries, chartData, showExtrapolation]);

  const minWeight = allWeights.length > 0 ? Math.min(...allWeights) : null;
  const maxWeight = allWeights.length > 0 ? Math.max(...allWeights) : null;

  // Add padding to Y axis
  const yMin = minWeight != null ? Math.floor(minWeight - 2) : 0;
  const yMax = maxWeight != null ? Math.ceil(maxWeight + 2) : 100;

  if (loading) {
    return <div className="dashboard"><div className="message">Loading...</div></div>;
  }

  if (error) {
    return <div className="dashboard"><div className="message">{error}</div></div>;
  }

  return (
    <div className="dashboard">
      <header className="header">
        <div className="current-weight">
          {latestWeight ? `${formatWeight(latestWeight)}` : '—'}
          <span className="unit">lbs</span>
        </div>
        <div className="header-right">
          <div className="username">{username}</div>
          <button
            className="projection-toggle"
            onClick={() => {
              setShowExtrapolation(prev => {
                if (!prev && normalizedEntries.length >= 2) {
                  const toDate = (ms) => new Date(ms).toISOString().slice(0, 10);
                  if (!rangeFrom) setRangeFrom(toDate(normalizedEntries[0].timestampMs));
                  if (!rangeTo) setRangeTo(toDate(normalizedEntries[normalizedEntries.length - 1].timestampMs));
                }
                return !prev;
              });
            }}
          >
            {showExtrapolation ? 'Hide Projection' : 'Show Projection'}
          </button>
        </div>
      </header>

      {showExtrapolation && (
        <div className="projection-panel">
          <label>
            From
            <input type="date" value={rangeFrom} onChange={e => setRangeFrom(e.target.value)} />
          </label>
          <label>
            To
            <input type="date" value={rangeTo} onChange={e => setRangeTo(e.target.value)} />
          </label>
          <label>
            Project
            <select value={projectionDays} onChange={e => setProjectionDays(Number(e.target.value))}>
              <option value={30}>30 days</option>
              <option value={60}>60 days</option>
              <option value={90}>90 days</option>
              <option value={180}>180 days</option>
              <option value={365}>1 year</option>
            </select>
          </label>
        </div>
      )}

      <div className="chart-container">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
            <XAxis
              dataKey="timestampMs"
              type="number"
              scale="time"
              domain={['dataMin', 'dataMax']}
              tickFormatter={(timestamp) => formatDate(new Date(timestamp))}
              tick={{ fontSize: 14, fill: '#000' }}
              axisLine={{ stroke: '#000', strokeWidth: 2 }}
              tickLine={{ stroke: '#000' }}
            />
            <YAxis
              domain={[yMin, yMax]}
              tick={{ fontSize: 14, fill: '#000' }}
              axisLine={{ stroke: '#000', strokeWidth: 2 }}
              tickLine={{ stroke: '#000' }}
              tickFormatter={(value) => `${value}`}
            />
            <Tooltip
              content={({ active, payload }) => {
                if (!active || !payload || !payload.length) return null;
                const data = payload[0].payload;
                const value = data.weight ?? data.projectedWeight;
                if (value == null) return null;
                return (
                  <div className="weight-tooltip">
                    {formatWeight(value)} lbs
                  </div>
                );
              }}
              cursor={false}
            />
            {latestWeight && (
              <ReferenceLine
                y={latestWeight}
                stroke="#000"
                strokeDasharray="5 5"
                strokeWidth={1}
              />
            )}
            <Line
              type="monotone"
              dataKey="weight"
              stroke="#000"
              strokeWidth={3}
              dot={{ fill: '#000', r: 4, strokeWidth: 0 }}
              activeDot={{ r: 6 }}
              connectNulls={false}
            />
            {showExtrapolation && (
              <Line
                type="monotone"
                dataKey="projectedWeight"
                stroke="#000"
                strokeWidth={1.5}
                strokeDasharray="6 4"
                dot={false}
                activeDot={false}
                connectNulls={false}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default App;
